import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Star, Award, Flame, TrendingUp, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChildDashboard = () => {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDashboard();
  }, [childId]);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/child/${childId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">Loading...</p>
      </div>
    );
  }

  const child = dashboardData?.child;
  const gamification = child?.gamification || {};

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/parent/dashboard')}
          className="mb-4 rounded-full"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Parent Dashboard
        </Button>

        <div className="card-base p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-2">
                {child?.name}'s Learning Space
              </h1>
              <p className="text-muted-foreground">{child?.grade}</p>
            </div>
            <div className="flex gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center">
                  <Star className="h-8 w-8 text-primary fill-primary" />
                  <span className="text-3xl font-bold ml-2">{gamification.stars || 0}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Stars</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center">
                  <Flame className="h-8 w-8 text-primary" />
                  <span className="text-3xl font-bold ml-2">{gamification.streak || 0}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Day Streak</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center">
                  <Award className="h-8 w-8 text-secondary" />
                  <span className="text-3xl font-bold ml-2">{gamification.level || 1}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">Level</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Available Tests */}
      <div className="max-w-7xl mx-auto">
        <h2 className="font-heading text-2xl font-bold mb-6">Available Tests</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboardData?.available_tests?.map((test) => (
            <div
              key={test.test_id}
              data-testid={`test-card-${test.test_id}`}
              className="card-interactive p-6"
            >
              <div className="mb-4">
                <h3 className="font-bold text-xl mb-2">{test.title}</h3>
                <div className="flex gap-2 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                    {test.subject}
                  </span>
                  <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold">
                    {test.difficulty}
                  </span>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Duration: {test.duration_minutes} minutes
              </p>
              <Button
                data-testid={`start-test-${test.test_id}`}
                onClick={() => navigate(`/test/${test.test_id}`)}
                className="w-full rounded-full"
              >
                Start Test
              </Button>
            </div>
          ))}
        </div>

        {/* Recent Results */}
        {dashboardData?.recent_results && dashboardData.recent_results.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-bold mb-6">Recent Tests</h2>
            <div className="grid gap-4">
              {dashboardData.recent_results.map((result) => (
                <div key={result.result_id} className="card-base p-6 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold">Test Completed</h3>
                    <p className="text-sm text-muted-foreground">
                      {new Date(result.completed_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Score</p>
                      <p className="font-bold text-lg text-secondary">
                        {result.score}/{result.total_questions}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Time</p>
                      <p className="font-bold text-lg">{result.time_taken_minutes} min</p>
                    </div>
                    <div className="flex">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <Star
                          key={idx}
                          className={`h-5 w-5 ${
                            idx < Math.round((result.score / result.total_questions) * 5)
                              ? 'text-primary fill-primary'
                              : 'text-muted'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChildDashboard;